"use client"

import { useState, useEffect } from "react"
import { getProductsByCategory } from "@/lib/api/dutchie"
import CategoryLayout from "@/components/shop/CategoryLayout"
import type { ProductDisplay } from "@/lib/api/dutchie"

/* ------------------------------------------------------------------ */
/*  Category Configuration                                            */
/* ------------------------------------------------------------------ */

const CATEGORIES = [
  "concentrates"
]

const EFFECTS = [
  "Relaxed", "Euphoric", "Happy", "Uplifted", "Creative",
  "Focused", "Energetic", "Sleepy", "Pain Relief", "Stress Relief"
]

// Concentrate type/consistency patterns
const SUBCATEGORY_REGEX: [RegExp,string][] = [
  [/live\s*resin/i, "Live Resin"],
  [/rosin/i, "Rosin"],
  [/shatter/i, "Shatter"],
  [/wax/i, "Wax"],
  [/budder|butter/i, "Budder"],
  [/sauce/i, "Sauce"],
  [/sugar/i, "Sugar"],
  [/diamonds?/i, "Diamonds"],
  [/crumble/i, "Crumble"],
  [/distillate/i, "Distillate"],
  [/hash/i, "Hash"],
  [/oil/i, "Oil"]
]

// Extraction method patterns
const EXTRACTION_REGEX: [RegExp,string][] = [
  [/solventless/i, "Solventless"],
  [/live\s*resin/i, "Live Resin"],
  [/rosin/i, "Rosin"],
  [/distillate/i, "Distillate"],
  [/co2/i, "CO2"],
  [/butane|bho/i, "Butane"],
  [/ethanol/i, "Ethanol"],
  [/ice\s*water|bubble/i, "Ice Water"]
]

const titleCase = (s: string) =>
  s.toLowerCase().split(" ").map(w => w[0].toUpperCase()+w.slice(1)).join(" ")

// Categorize concentrate by consistency/type
const categorizeConcentrate = (p: ProductDisplay) => {
  const text = `${p.name ?? ""} ${p.description ?? ""} ${p.subcategory ?? ""}`.toLowerCase()
  
  // Check for specific concentrate types
  for (const [regex, category] of SUBCATEGORY_REGEX) {
    if (regex.test(text)) {
      return category
    }
  }
  
  // Default categorization
  if (text.includes('concentrate') || text.includes('extract')) {
    return "Extract"
  }
  
  return "Other"
}

// Extract extraction method
const extractExtractionMethod = (p: ProductDisplay) => {
  const text = `${p.name ?? ""} ${p.description ?? ""}`.toLowerCase()
  
  for (const [regex, method] of EXTRACTION_REGEX) {
    if (regex.test(text)) {
      return method
    }
  }
  
  return "Other"
}

// Extract consistency/physical form
const extractConsistency = (p: ProductDisplay) => {
  const text = `${p.name ?? ""} ${p.description ?? ""} ${p.subcategory ?? ""}`.toLowerCase()
  
  // Check for specific consistencies
  if (text.includes('shatter')) return 'Shatter'
  if (text.includes('wax')) return 'Wax'
  if (text.includes('budder') || text.includes('butter')) return 'Budder'
  if (text.includes('sauce')) return 'Sauce'
  if (text.includes('sugar')) return 'Sugar'
  if (text.includes('diamond')) return 'Diamonds'
  if (text.includes('crumble')) return 'Crumble'
  if (text.includes('live resin')) return 'Live Resin'
  if (text.includes('rosin')) return 'Rosin'
  if (text.includes('oil')) return 'Oil'
  if (text.includes('hash')) return 'Hash'
  if (text.includes('distillate')) return 'Distillate'
  
  return 'Other'
}

// Extract potency value (THC/CBD)
const extractPotencyValue = (product: ProductDisplay, type: 'thc' | 'cbd'): number => {
  // First try direct fields
  const directValue = type === 'thc' ? product.thc : product.cbd;
  if (directValue) {
    const match = directValue.match(/(\d+(?:\.\d+)?)/);
    if (match && match[1]) {
      return parseFloat(match[1]);
    }
  }

  // Then try potency objects
  const potencyObj = type === 'thc' ? (product as any).potencyThc : (product as any).potencyCbd;
  if (potencyObj && potencyObj.formatted) {
    const match = potencyObj.formatted.match(/(\d+(?:\.\d+)?)/);
    if (match && match[1]) {
      return parseFloat(match[1]);
    }
  }

  // Finally try to extract from text
  const productText = `${product.name || ''} ${product.description || ''}`.toLowerCase();
  const regex = new RegExp(`${type}:?\\s*(\\d+(?:\\.\\d+)?)\\s*%`, 'i');
  const match = productText.match(regex);

  if (match && match[1]) {
    return parseFloat(match[1]);
  }

  return 0; // Default to 0 if no value found
}

/* ------------------------------------------------------------------ */
/*  Page                                                              */
/* ------------------------------------------------------------------ */

export default function ConcentratesPage() {
  // Custom fetch function for concentrate products
  const fetchProducts = async (): Promise<ProductDisplay[]> => {
    try {
      console.log('Fetching products from categories:', CATEGORIES)
      
      // Get all products and filter manually
      const { getProducts } = await import('@/lib/api/dutchie')
      const allProducts = await getProducts()
      
      console.log(`Total products available: ${allProducts.length}`)
      
      // Filter for CONCENTRATES category
      const concentrateProducts = allProducts.filter(product => {
        const category = product.category.toUpperCase()
        return category === 'CONCENTRATES'
      })
      
      console.log(`Concentrate products found: ${concentrateProducts.length}`)
      
      // Deduplicate by ID
      const uniqueProductsMap = new Map<string, ProductDisplay>()
      concentrateProducts.forEach(product => {
        if (!uniqueProductsMap.has(product.id)) {
          uniqueProductsMap.set(product.id, product)
        }
      })
      
      const uniqueProducts = Array.from(uniqueProductsMap.values())
      console.log(`Unique products after deduplication: ${uniqueProducts.length}`)
      
      // Enhance products with virtual subcategory and extracted potency values
      const enhancedProducts = uniqueProducts.map((product, index) => {
        const thcValue = extractPotencyValue(product, 'thc')
        const cbdValue = extractPotencyValue(product, 'cbd')
        
        // Debug log for first few products
        if (index < 3) {
          console.log('Concentrates THC/CBD extraction:', {
            name: product.name,
            potencyThc: (product as any).potencyThc,
            potencyCbd: (product as any).potencyCbd,
            extractedThc: thcValue,
            extractedCbd: cbdValue
          })
        }
        
        return {
          ...product,
          virtualSubcategory: categorizeConcentrate(product),
          extractionMethod: extractExtractionMethod(product),
          // Extract THC/CBD numeric values for filtering
          thc: thcValue.toString() + '%',
          cbd: cbdValue.toString() + '%'
        }
      })
      
      // Debug subcategory distribution
      const subcounts: Record<string, number> = {}
      enhancedProducts.forEach(product => {
        const sub = (product as any).virtualSubcategory
        subcounts[sub] = (subcounts[sub] || 0) + 1
      })
      console.log('Enhanced concentrates subcategory distribution:', subcounts)
      
      console.log('fetchProducts returning enhanced products:', enhancedProducts.length)
      
      return enhancedProducts
      
    } catch (error) {
      console.error('Error fetching concentrate products:', error)
      return []
    }
  }
  
  const config = {
    title: "Concentrates",
    blurb: "Premium cannabis concentrates including shatter, wax, live resin, rosin, and distillates",
    fetch: fetchProducts,

    effects: EFFECTS,
    potencyUnit: "%",
    showPotency: true,
    showWeight: false, // Concentrates usually sold by fixed amounts, not variable weights
    filterInStock: false,
    headerTheme: "fuchsia" as const, // Fuchsia theme for concentrates
    extraFilterChips: (product: ProductDisplay) => {
      const extractionMethod = extractExtractionMethod(product);
      const consistency = extractConsistency(product);
      return {
        'Extraction Methods': extractionMethod !== 'Other' ? [extractionMethod] : [],
        'Consistencies': consistency !== 'Other' ? [consistency] : []
      };
    },
    debug: true
  }

  return (
    <CategoryLayout
      config={config}
      productCardProps={{ 
        buttonClassName: "bg-fuchsia-600 hover:bg-fuchsia-700"
      }}
    />
  )
}